<template>
  <div class="pds">

    <!-- BANNER FOCO TEMPORAL -->
    <div v-if="showBanner" class="pds__banner">
      <span><i class="fa-solid fa-circle-info"></i> <strong>Foco Temporal Activo:</strong> Mostrando información de <strong>{{ activeYearsText }}</strong>.</span>
      <button @click="showBanner = false"><i class="fa-solid fa-xmark"></i></button>
    </div>

    <!-- KPIs -->
    <div class="pds__kpis">
      <div 
        class="pds__kpi" 
        v-for="k in kpis" 
        :key="k.id"
        :class="{ 'pds__kpi--clickable': k.id === 'oc_open' || k.id === 'oc_saldo' }"
        @click="handleKpiClick(k)"
      >
        <div class="pds__kpi-top">
          <span class="pds__kpi-label">{{ k.label }}</span>
          <i :class="['fa-solid', k.icon]" :style="{ color: k.color }"></i>
        </div>
        <h3 class="pds__kpi-value">{{ k.value }}</h3>
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px;">
           <p class="pds__kpi-sub" style="margin: 0 !important;">{{ k.sub }}</p>
           <div class="pds__kpi-var" v-if="k.var" :class="k.var.isUp ? 'pds__var-up' : 'pds__var-down'">
              <i :class="k.var.isUp ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'"></i>
              <span v-if="k.id === 'provs' || k.id === 'skus'">{{ k.var.rawDiff }}</span>
              <span v-else>{{ k.var.pct }}%</span>
              <span class="pds__var-ref" v-if="k.ref">vs {{ k.ref }}</span>
           </div>
        </div>
      </div>
    </div>

    <!-- GRÁFICOS FILA 1 -->
    <div class="pds__charts-row">
      <div class="pds__chart-card">
        <div class="pds__card-head"><h3>ESTADO DE OC (N° ÓRDENES)</h3><p>Proporción de atención actual</p></div>
        <div class="pds__chart-wrap pds__chart-wrap--sm"><canvas ref="chartEstado"></canvas></div>
      </div>
      <div class="pds__chart-card">
        <div class="pds__card-head"><h3>OC CON SALDO POR PROVEEDOR (TOP 10)</h3><p>Distribución de documentos con saldo pendiente</p></div>
        <div class="pds__chart-wrap"><canvas ref="chartSaldo"></canvas></div>
      </div>
      <div class="pds__chart-card">
        <div class="pds__card-head"><h3>MONTO {{ textBought.toUpperCase() }} POR PROVEEDOR (USD) - TOP 10</h3><p>Concentración de facturación en dólares</p></div>
        <div class="pds__chart-wrap"><canvas ref="chartMonto"></canvas></div>
      </div>
      <div class="pds__chart-card">
        <div class="pds__card-head"><h3>EVOLUCIÓN MENSUAL (MONTO USD)</h3><p>Flujo consolidado de {{ textPurchases.toLowerCase() }} del período</p></div>
        <div class="pds__chart-wrap"><canvas ref="chartEvolucion"></canvas></div>
      </div>
    </div>

    <!-- GRÁFICOS FILA 2 -->
    <div class="pds__charts-row2">
      <div class="pds__chart-card">
        <div class="pds__card-head"><h3>MONTO {{ textBought.toUpperCase() }} POR RUBRO (USD)</h3><p>Monto agrupado por categoría principal (Top 5)</p></div>
        <div class="pds__chart-wrap pds__chart-wrap--sm"><canvas ref="chartRubro"></canvas></div>
      </div>

      <div class="pds__chart-card">
        <div class="pds__card-head"><h3>OC ABIERTAS POR ANTIGÜEDAD (DÍAS)</h3><p>Envejecimiento de órdenes en proceso</p></div>
        <div class="pds__aging">
          <div class="pds__aging-cell" v-for="ag in aging" :key="ag.label">
            <span class="pds__aging-label">{{ ag.label }}</span>
            <strong class="pds__aging-count">{{ ag.count }}</strong>
            <span class="pds__aging-pct">({{ ag.pct }}%)</span>
          </div>
        </div>
        <p class="pds__aging-note">Enfoque prioritario en OC con saldo de más de 30 días.</p>
      </div>

      <div class="pds__chart-card">
        <div class="pds__card-head"><h3>MONEDA Y BALANCE DE SALDOS PENDIENTES</h3><p>Totalización de compromisos financieros por pagar</p></div>
        <div class="pds__balance">
          <div class="pds__balance-chart"><canvas ref="chartMoneda"></canvas></div>
          <div class="pds__balance-info">
            <div class="pds__balance-card">
              <div class="pds__balance-card-top"><i class="fa-solid fa-wallet"></i><span>Saldo Pendiente USD</span></div>
              <h4 class="pds__balance-value">{{ formatUSD(totalPendingBalance) }}</h4>
              <span class="pds__balance-hint">Por recibir y valorizar en almacén</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <DashboardTable
      title="Historial Consolidado de Órdenes"
      subtitle="Visualización de columnas originales del archivo: N° OC, Rubros, SKU y Precios USD"
      :items="dataset"
      :searchKeys="['OrdDocumentNumber', 'DocNumSap', 'SupName', 'ItemDescription', 'ItemCode', 'CategoryName']"
      :filterableColumns="[{ key: 'OrdStatusName', label: 'Todos los estados' }]"
      :itemsPerPage="perPage"
      :pagination="true"
      :page="page"
      :search="tableSearch"
      :searchPlaceholder="'Buscar orden, proveedor o ' + textMaterial.toLowerCase() + '...'"
      @update:search="tableSearch = $event"
      @update:page="page = $event"
      :headers="tableHeaders"
    >
      <template #row="{ item: row }">
        <tr :key="row.OrdDocumentNumber + '-' + row.ItemCode">
          <td><span class="pds__badge-oc">{{ row.OrdDocumentNumber }}</span></td>
          <td><span class="pds__badge-oc">{{ row.DocNumSap || '-' }}</span></td>
          <td>{{ row.OrdDate }}</td>
          <td>{{ row.SupName }}</td>
          <td>{{ row.CategoryName }}</td>
          <td class="pds__mono">{{ row.ItemCode }}</td>
          <td class="pds__desc">{{ row.ItemDescription }}</td>
          <td class="text-right pds__mono">{{ row.Quantity ? row.Quantity.toLocaleString() : '-' }}</td>
          <td class="text-right pds__mono">${{ row.UnitPrice ? row.UnitPrice.toFixed(4) : '-' }}</td>
          <td class="text-right pds__mono">${{ row.TotalAmount ? row.TotalAmount.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}) : '-' }}</td>
          <td class="text-center"><span :class="['pds__estado', statusClass(row.OrdStatus, row.OrdStatusName)]">{{ row.OrdStatusName }}</span></td>
        </tr>
      </template>
    </DashboardTable>
    <!-- MODAL EXTERNO DE KPI -->
    <DashboardKpiModal 
      v-if="modal.show" 
      :title="modal.title" 
      :data="modal.data" 
      @close="modal.show = false" 
    />
  </div>
</template>

<script>
import DashboardKpiModal from './DashboardKpiModal.vue';

const GREEN  = '#0A5C36';
const YELLOW = '#FBBF24';
const RED    = '#EF4444';
const SLATE  = '#94A3B8';

import DashboardTable from './DashboardTable.vue';
import _sOrderPurchase from "@/services/Logistics/OrderPurchaseService";

export default {
  name: 'DashboardSummary',
  components: {
    DashboardKpiModal,
    DashboardTable
  },
  props: { 
    dataset: { type: Array, required: true },
    orderType: { type: [String, Number], default: null },
    isDark: { type: Boolean, default: false }
  },
  data() {
    return {
      showBanner: true,
      page: 1,
      perPage: 10,
      charts: {},
      tableSearch: '',
      modal: {
        show: false,
        title: '',
        data: []
      },
      categoriesMap: new Map()
    };
  },
  computed: {
    isService() { return String(this.orderType) === '3'; },
    textBought() { return this.isService ? 'Ejecutado' : 'Comprado'; },
    textPurchases() { return this.isService ? 'servicios' : 'compras'; },
    textMaterial() { return this.isService ? 'Servicio' : 'Material'; },
    
    activeYearsText() {
      if (!this.dataset || this.dataset.length === 0) return 'Ningún año disponible';
      const years = new Set(this.dataset.map(r => {
        if (!r.OrdDate) return null;
        const parts = r.OrdDate.split('/');
        if (parts.length === 3) return parts[2].split(' ')[0]; // En caso de que haya hora
        return null;
      }).filter(y => y !== null));
      
      const sortedYears = Array.from(years).sort();
      if (sortedYears.length === 0) return 'Sin fechas registradas';
      if (sortedYears.length === 1) return sortedYears[0];
      if (sortedYears.length === 2) return `${sortedYears[0]} y ${sortedYears[1]}`;
      const last = sortedYears.pop();
      return `${sortedYears.join(', ')} y ${last}`;
    },
    tableHeaders() {
      return [
        { label: 'N° OC SUN EXPERT', key: 'OrdDocumentNumber', sortable: true },
        { label: 'N° PEDIDO SAP', key: 'DocNumSap', sortable: true },
        { label: 'Fecha OC', key: 'OrdDate', sortable: true },
        { label: 'Proveedor', key: 'SupName', sortable: true },
        { label: 'Rubro', key: 'CategoryName', sortable: true },
        { label: 'Código / SKU', key: 'ItemCode', sortable: true },
        { label: 'Descripción del ' + this.textMaterial, key: 'ItemDescription', sortable: true },
        { label: 'Cant.', key: 'Quantity', sortable: true, align: 'right' },
        { label: 'P. Unit. $', key: 'UnitPrice', sortable: true, align: 'right' },
        { label: 'P. Total $', key: 'TotalAmount', sortable: true, align: 'right' },
        { label: 'Estado', key: 'OrdStatusName', sortable: true, align: 'center' }
      ];
    },
    totalOrders()       { return new Set(this.dataset.map(r => r.OrdDocumentNumber)).size || 0; },
    totalAmount()       { return this.dataset.reduce((a, r) => a + (r.TotalAmount || 0), 0); },
    activeSuppliers()   { return new Set(this.dataset.map(r => r.SupName)).size; },
    uniqueSkus()        { return new Set(this.dataset.map(r => r.ItemCode)).size; },
    openOrders()        { return new Set(this.dataset.filter(r => this.isOpen(r)).map(r => r.OrdDocumentNumber)).size || 0; },
    ordersWithBalance() { return new Set(this.dataset.filter(r => (r.PendingBalance || 0) > 0).map(r => r.OrdDocumentNumber)).size || 0; },
    averageTicket()     { return this.totalOrders ? this.totalAmount / this.totalOrders : 0; },
    totalPendingBalance() { return this.dataset.reduce((a, r) => a + (r.PendingBalance || 0), 0); },
    monthlyData() {
      if (!this.dataset) return [];
      const map = new Map();
      this.dataset.forEach(r => {
        if (!r.OrdDate) return;
        const parts = r.OrdDate.split(' ')[0].split('/');
        if (parts.length === 3) {
          const monthKey = `${parts[2]}-${parts[1].padStart(2, '0')}`;
          if (!map.has(monthKey)) {
             map.set(monthKey, {
                key: monthKey, month: parts[1],
                totalAmount: 0, orders: new Set(), suppliers: new Set(), skus: new Set(),
                openOrders: new Set(), balanceOrders: new Set()
             });
          }
          const m = map.get(monthKey);
          m.totalAmount += (r.TotalAmount || 0);
          m.orders.add(r.OrdDocumentNumber);
          m.suppliers.add(r.SupName);
          m.skus.add(r.ItemCode);
          if (this.isOpen(r)) m.openOrders.add(r.OrdDocumentNumber);
          if ((r.PendingBalance || 0) > 0) m.balanceOrders.add(r.OrdDocumentNumber);
        }
      });
      return Array.from(map.values()).sort((a,b) => a.key.localeCompare(b.key));
    },
    variationData() {
      const data = this.monthlyData;
      if (data.length < 2) return null;
      const current = data[data.length - 1];
      const prev = data[data.length - 2];
      
      const monthNames = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
      const prevMonthName = monthNames[parseInt(prev.month, 10) - 1] || 'mes anterior';
      
      const calcVar = (currVal, prevVal) => {
         if (prevVal === 0 && currVal > 0) return { pct: 100, isUp: true, rawDiff: currVal };
         if (prevVal === 0 && currVal === 0) return { pct: 0, isUp: true, rawDiff: 0 };
         const diff = currVal - prevVal;
         const pct = (diff / prevVal) * 100;
         return { pct: Math.abs(Math.round(pct)), isUp: diff >= 0, rawDiff: Math.abs(diff) };
      };
      
      return {
         prevMonthName,
         totalOrders: calcVar(current.orders.size, prev.orders.size),
         totalAmount: calcVar(current.totalAmount, prev.totalAmount),
         activeSuppliers: calcVar(current.suppliers.size, prev.suppliers.size),
         uniqueSkus: calcVar(current.skus.size, prev.skus.size),
         openOrders: calcVar(current.openOrders.size, prev.openOrders.size),
         ordersWithBalance: calcVar(current.balanceOrders.size, prev.balanceOrders.size),
         averageTicket: calcVar(
            current.orders.size ? current.totalAmount / current.orders.size : 0, 
            prev.orders.size ? prev.totalAmount / prev.orders.size : 0
         )
      };
    },
    kpis() {
      const v = this.variationData;
      return [
        { id: 'total_oc',   label: 'TOTAL OC',         icon: 'fa-file-invoice',  color: SLATE,   value: this.totalOrders,                                                       sub: 'N° Órdenes', var: v?.totalOrders, ref: v?.prevMonthName },
        { id: 'monto',      label: 'MONTO COMPRADO',   icon: 'fa-dollar-sign',   color: '#10B981',value: this.formatUSD(this.totalAmount),                                    sub: 'TOTAL USD', var: v?.totalAmount, ref: v?.prevMonthName },
        { id: 'provs',      label: 'PROVEEDORES ACT.', icon: 'fa-handshake',     color: SLATE,   value: this.activeSuppliers,                                                    sub: 'PROVEEDORES', var: v?.activeSuppliers, ref: v?.prevMonthName },
        { id: 'skus',       label: 'SKU COMPRADOS',    icon: 'fa-box-open',      color: SLATE,   value: this.uniqueSkus,                                                     sub: 'SKU', var: v?.uniqueSkus, ref: v?.prevMonthName },
        { id: 'oc_open',    label: 'OC ABIERTAS',      icon: 'fa-lock-open',     color: YELLOW,  value: this.openOrders,                                                     sub: 'N° Órdenes', var: v?.openOrders, ref: v?.prevMonthName },
        { id: 'oc_saldo',   label: 'OC CON SALDO',     icon: 'fa-receipt',       color: '#F97316',value: this.ordersWithBalance,                                                    sub: 'N° Órdenes', var: v?.ordersWithBalance, ref: v?.prevMonthName },
        { id: 'ticket',     label: 'VALORIZADO POR OC',  icon: 'fa-calculator',    color: SLATE,   value: this.formatUSD(this.averageTicket),                                 sub: 'POR OC', var: v?.averageTicket, ref: v?.prevMonthName },
      ];
    },
    aging() {
      const open = this.dataset.filter(r => this.isOpen(r));
      const uniqueOpen = new Map();
      open.forEach(r => {
        if (!uniqueOpen.has(r.OrdDocumentNumber)) uniqueOpen.set(r.OrdDocumentNumber, r);
      });
      const total = uniqueOpen.size || 1;
      const count = [0,0,0,0];
      uniqueOpen.forEach(r => {
        const d = r.AgingDays || 0;
        if      (d <= 15)  count[0]++;
        else if (d <= 30)  count[1]++;
        else if (d <= 60)  count[2]++;
        else               count[3]++;
      });
      const labels = ['0-15 D','16-30 D','31-60 D','+60 D'];
      return labels.map((label,i) => ({ label, count: count[i], pct: ((count[i]/total)*100).toFixed(0) }));
    },
  },
  watch: {
    dataset() { this.page = 1; this.$nextTick(() => this.renderCharts()); },
    isDark() { this.$nextTick(() => this.renderCharts()); }
  },
  mounted() {
    this.fetchCategories();
    this.initCharts();
  },
  beforeDestroy() {
    Object.values(this.charts).forEach(c => c && c.destroy());
  },
  methods: {
    async fetchCategories() {
       try {
          const res = await _sOrderPurchase.getCategory();
          if (res && res.data) {
             const map = new Map();
             res.data.forEach(c => {
                const code = c.Code || c.CategoryCode || c.CategoryName;
                const name = c.Name || c.CategoryName;
                if (code) map.set(code, name);
             });
             this.categoriesMap = map;
             this.buildCategoryChart();
          }
       } catch (err) {
          console.error("Error al obtener rubros:", err);
       }
    },
    initCharts() {
        this.$nextTick(() => this.renderCharts());
    },
    isOpen(row) { 
      const name = (row.OrdStatusName || '').toUpperCase();
      if (name.startsWith('CERR')) return false;
      if (row.OrdStatus == null) return false;
      return [1, 2, 4, 5, 7, 10].includes(Number(row.OrdStatus));
    },
    formatUSD(v) {
      if (v === undefined || v === null || isNaN(v)) return 'USD 0';
      if (v >= 1_000_000) return `USD ${(v/1_000_000).toFixed(2)} MM`;
      if (v >= 1_000)     return `USD ${Math.round(v/1_000)}K`;
      return `USD ${Number(v).toFixed(0)}`;
    },
    statusClass(st, name) {
      const u = (name || '').toUpperCase();
      if (u.startsWith('CERR')) return 'pds__estado--closed';

      if (st == null) return '';
      const id = Number(st);
      if (id === 9) return 'pds__estado--closed';
      if ([1, 2, 4, 5, 7, 10].includes(id)) return 'pds__estado--open';
      if ([3, 6, 8].includes(id)) return 'pds__estado--canceled';
      return '';
    },
    handleKpiClick(kpi) {
      if (kpi.id === 'oc_open') {
        this.modal.title = 'Órdenes Abiertas';
        this.modal.data = this.dataset.filter(r => this.isOpen(r));
        this.modal.show = true;
      } else if (kpi.id === 'oc_saldo') {
        this.modal.title = 'Órdenes con Saldo Pendiente';
        this.modal.data = this.dataset.filter(r => (r.PendingBalance || 0) > 0);
        this.modal.show = true;
      }
    },
    renderCharts() {
      if (typeof Chart === 'undefined') { setTimeout(() => this.renderCharts(), 500); return; }
      this.buildStatusChart();
      this.buildBalanceChart();
      this.buildAmountChart();
      this.buildEvolutionChart();
      this.buildCategoryChart();
      this.buildCurrencyChart();
    },
    mkChart(ref, type, data, options = {}) {
      if (this.charts[ref]) { this.charts[ref].destroy(); }
      const ctx = this.$refs[ref];
      if (!ctx) return;
      
      const tc = this.isDark ? '#F8FAFC' : '#64748B';
      const gc = this.isDark ? '#334155' : '#E2E8F0';

      const mergedOptions = {
        responsive: true,
        maintainAspectRatio: false,
        color: tc,
        ...options
      };

      if (!mergedOptions.plugins) mergedOptions.plugins = {};
      if (!mergedOptions.plugins.legend) mergedOptions.plugins.legend = {};
      if (!mergedOptions.plugins.legend.labels) mergedOptions.plugins.legend.labels = { font: { size: 10, family: 'Inter' }, boxWidth: 10 };
      mergedOptions.plugins.legend.labels.color = tc;

      if (mergedOptions.scales) {
        for (const axis in mergedOptions.scales) {
          if (!mergedOptions.scales[axis].ticks) mergedOptions.scales[axis].ticks = {};
          mergedOptions.scales[axis].ticks.color = tc;
          
          if (!mergedOptions.scales[axis].grid) mergedOptions.scales[axis].grid = {};
          mergedOptions.scales[axis].grid.color = gc;
        }
      }

      this.charts[ref] = new Chart(ctx, { type, data, options: mergedOptions });
    },
    buildStatusChart() {
      const oc = this.dataset;
      const cerradas  = oc.filter(r => Number(r.OrdStatus) === 9 || (r.OrdStatusName||'').toUpperCase().startsWith('CERR')).length;
      const abiertas  = oc.filter(r => this.isOpen(r)).length;
      const anuladas  = oc.filter(r => [3, 6, 8].includes(Number(r.OrdStatus))).length;
      this.mkChart('chartEstado','doughnut',{
        labels:['Cerrada','Abierta','Anulada'],
        datasets:[{ data:[cerradas,abiertas,anuladas], backgroundColor:['#10B981','#FBBF24','#EF4444'], borderWidth: 0, hoverOffset: 4 }]
      },{ cutout:'65%', plugins:{ legend:{ position:'bottom', labels:{ font:{size:10,family:'Inter'}, boxWidth:10, color: '#94A3B8' } } } });
    },
    buildBalanceChart() {
      const byProv = {};
      this.dataset.filter(r => (r.PendingBalance||0) > 0).forEach(r => { byProv[r.SupName] = (byProv[r.SupName]||0)+1; });
      const sorted = Object.entries(byProv).sort((a,b)=>b[1]-a[1]).slice(0,10);
      this.mkChart('chartSaldo','bar',{
        labels: sorted.map(x=>x[0]),
        datasets:[{ data: sorted.map(x=>x[1]), backgroundColor: YELLOW, borderRadius: 4 }]
      },{ indexAxis:'y', plugins:{legend:{display:false}}, scales:{ x:{ ticks:{font:{size:9}} }, y:{ ticks:{font:{size:9}, autoSkip: false} } } });
    },
    buildAmountChart() {
      const byProv = {};
      this.dataset.forEach(r => { byProv[r.SupName] = (byProv[r.SupName]||0)+(r.TotalAmount||0); });
      const sorted = Object.entries(byProv).sort((a,b)=>b[1]-a[1]).slice(0,10);
      this.mkChart('chartMonto','bar',{
        labels: sorted.map(x=>x[0]),
        datasets:[{ data: sorted.map(x=>x[1]), backgroundColor: GREEN, borderRadius: 4 }]
      },{ indexAxis:'y', plugins:{legend:{display:false}}, scales:{ x:{ ticks:{font:{size:9}, callback: v => v>=1000?`${(v/1000).toFixed(0)}K`:v} }, y:{ ticks:{font:{size:9}, autoSkip: false} } } });
    },
    buildEvolutionChart() {
      const byMonth = {};
      this.dataset.forEach(r => {
        if (!r.OrdDate) return;
        let key = '';
        if (r.OrdDate.includes('/')) {
           const parts = r.OrdDate.split('/'); // DD, MM, YYYY
           if (parts.length === 3) key = `${parts[2]}-${parts[1]}`;
        } else {
           key = r.OrdDate.substring(0,7); // YYYY-MM fallback
        }
        if (key) byMonth[key] = (byMonth[key]||0)+(r.TotalAmount||0);
      });
      const sorted = Object.entries(byMonth).sort((a,b)=>a[0].localeCompare(b[0]));
      this.mkChart('chartEvolucion','line',{
        labels: sorted.map(x=>x[0]),
        datasets:[{ data: sorted.map(x=>x[1]), borderColor: GREEN, backgroundColor: 'rgba(10,92,54,.08)', tension:.35, pointRadius:3, pointBackgroundColor:GREEN, fill:true }]
      },{ plugins:{legend:{display:false}}, scales:{ x:{ticks:{font:{size:9}}}, y:{ticks:{font:{size:9}, callback: v=>`$${(v/1000).toFixed(0)}K`}} } });
    },
    buildCategoryChart() {
      const byRubro = {};
      this.dataset.forEach(r => {
        let cat = 'OTROS';
        if (r.CategoryCode) {
           cat = this.categoriesMap.get(r.CategoryCode) || r.CategoryCode;
        }
        cat = cat.toUpperCase();
        byRubro[cat] = (byRubro[cat]||0) + (r.TotalAmount||0);
      });
      
      let sorted = Object.entries(byRubro).sort((a,b) => b[1] - a[1]);
      let finalEntries = sorted.slice(0, 5);
      
      if (sorted.length > 5) {
          const sumOtros = sorted.slice(5).reduce((acc, curr) => acc + curr[1], 0);
          if (sumOtros > 0) finalEntries.push(['OTROS', sumOtros]);
      }

      const colors = { EMBALAJE: GREEN, EMPAQUE: YELLOW, PARIHUELA: '#F97316', OTRO: SLATE, OTROS: SLATE };
      
      this.mkChart('chartRubro','bar',{
        labels: finalEntries.map(x=>x[0]),
        datasets:[{ data: finalEntries.map(x=>x[1]), backgroundColor: finalEntries.map(x=>colors[x[0]]||GREEN), borderRadius:4 }]
      },{ indexAxis:'y', plugins:{legend:{display:false}}, scales:{ x:{ ticks:{font:{size:9}, callback: v=>v>=1000?`${(v/1000).toFixed(0)}K`:v} }, y:{ticks:{font:{size:9}, autoSkip: false}} } });
    },
    buildCurrencyChart() {
      const usd = this.dataset.filter(r=>(r.CurrencyName||'').toLowerCase().includes('dólar') || (r.CurrencyName||'').toLowerCase() === 'usd').length;
      const other = this.dataset.length - usd;
      this.mkChart('chartMoneda','doughnut',{
        labels:['USD','Otros'],
        datasets:[{ data:[usd,other||1], backgroundColor:['#3B82F6','#F59E0B'], borderWidth: 0, hoverOffset: 4 }]
      },{ cutout:'60%', plugins:{legend:{display:false}} });
    },
  },
};
</script>

<style scoped>
.pds { display: flex !important; flex-direction: column !important; gap: 20px !important; }

/* BANNER */
.pds__banner {
  display: flex !important; align-items: center !important; justify-content: space-between !important;
  background: #ECFDF5 !important; border: 1px solid #A7F3D0 !important; color: #065F46 !important;
  padding: 10px 16px !important; border-radius: 12px !important; font-size: 12px !important;
}
.pds__banner button { background: none !important; border: none !important; cursor: pointer !important; color: #059669 !important; font-size: 14px !important; }

/* KPIs */
.pds__kpis { display: grid !important; grid-template-columns: repeat(7,1fr) !important; gap: 12px !important; }
@media(max-width:1200px){ .pds__kpis{ grid-template-columns: repeat(4,1fr) !important; } }
@media(max-width:768px) { .pds__kpis{ grid-template-columns: repeat(2,1fr) !important; } }

.pds__kpi {
  background: #fff !important; border: 1px solid #F1F5F9 !important;
  border-radius: 16px !important; padding: 14px !important; box-shadow: 0 1px 3px rgba(0,0,0,.06) !important;
  display: flex !important; flex-direction: column !important; gap: 4px !important;
}
.pds__kpi-top { display: flex !important; align-items: center !important; justify-content: space-between !important; }
.pds__kpi-top i { font-size: 14px !important; }
.pds__kpi-label { font-size: 9px !important; font-weight: 700 !important; color: #94A3B8 !important; text-transform: uppercase !important; letter-spacing: .8px !important; }
.pds__kpi-value { font-size: 18px !important; font-weight: 800 !important; color: #1E293B !important; margin-top: 6px !important; }
.pds__kpi-sub   { font-size: 9px !important; color: #94A3B8 !important; font-weight: 500 !important; }

/* GRÁFICOS */
.pds__charts-row  { display: grid !important; grid-template-columns: repeat(4,1fr) !important; gap: 16px !important; min-width: 0 !important; }
.pds__charts-row2 { display: grid !important; grid-template-columns: repeat(3,1fr) !important; gap: 16px !important; min-width: 0 !important; }
.pds__kpi-var { display: flex !important; align-items: center !important; gap: 4px !important; font-size: 10px !important; font-weight: 700 !important; padding: 2px 6px !important; border-radius: 4px !important; }
.pds__var-up { background: rgba(16, 185, 129, 0.1) !important; color: #059669 !important; }
.pds__var-down { background: rgba(239, 68, 68, 0.1) !important; color: #DC2626 !important; }
.pds__var-ref { font-weight: 500 !important; font-size: 9px !important; margin-left: 2px !important; opacity: 0.8 !important; }

@media(max-width:1200px){ .pds__charts-row{ grid-template-columns: repeat(2,1fr) !important; } .pds__charts-row2{ grid-template-columns: 1fr !important; } }
@media(max-width:768px) { .pds__charts-row{ grid-template-columns: 1fr !important; } }

.pds__chart-card {
  background: var(--pd-card-bg, #fff) !important; border: 1px solid var(--pd-border, #E2E8F0) !important;
  border-radius: 16px !important; padding: 18px !important; box-shadow: 0 1px 3px rgba(0,0,0,.06) !important;
  display: flex !important; flex-direction: column !important; gap: 10px !important; min-height: 280px !important;
  min-width: 0 !important; overflow: hidden !important;
}
.pds__card-head h3 { font-size: 10px !important; font-weight: 700 !important; color: #1E293B !important; text-transform: uppercase !important; letter-spacing: .6px !important; }
.pds__card-head p  { font-size: 9px !important; color: #94A3B8 !important; margin-top: 2px !important; }
.pds__chart-wrap     { position: relative !important; flex: 1 !important; min-height: 160px !important; min-width: 0 !important; width: 100% !important; }
.pds__chart-wrap--sm { position: relative !important; flex: 1 !important; min-height: 130px !important; min-width: 0 !important; width: 100% !important; }

/* AGING */
.pds__aging { display: grid !important; grid-template-columns: repeat(4,1fr) !important; gap: 8px !important; margin-top: 8px !important; }
.pds__aging-cell { background: var(--pd-bg, #F8FAFC) !important; border: 1px solid var(--pd-border, #E2E8F0) !important; border-radius: 10px !important; padding: 10px 6px !important; text-align: center !important; }
.pds__aging-label { font-size: 9px !important; font-weight: 700 !important; color: var(--pd-text-muted, #64748B) !important; text-transform: uppercase !important; display: block !important; }
.pds__aging-count { font-size: 16px !important; font-weight: 800 !important; color: var(--pd-text, #1E293B) !important; display: block !important; margin-top: 4px !important; }
.pds__aging-pct   { font-size: 9px !important; color: var(--pd-text-muted, #94A3B8) !important; display: block !important; }
.pds__aging-note  { font-size: 9px !important; color: var(--pd-text-muted, #94A3B8) !important; font-style: italic !important; text-align: center !important; }

/* BALANCE */
.pds__balance { display: flex !important; align-items: center !important; gap: 24px !important; margin-top: 16px !important; flex: 1 !important; }
.pds__balance-chart { width: 130px !important; height: 130px !important; flex-shrink: 0 !important; }
.pds__balance-info { flex: 1 !important; display: flex !important; flex-direction: column !important; justify-content: center !important; }
.pds__balance-card  { background: var(--pd-sidebar-bg, #F1F5F9) !important; border: 1px solid var(--pd-border, #E2E8F0) !important; border-radius: 12px !important; padding: 16px !important; width: 100% !important; }
.pds__balance-card-top { display: flex !important; align-items: center !important; gap: 6px !important; color: var(--pd-text-muted, #64748B) !important; margin-bottom: 6px !important; }
.pds__balance-card-top i { color: var(--pd-yellow, #F59E0B) !important; font-size: 14px !important; }
.pds__balance-card-top span { font-size: 10px !important; font-weight: 700 !important; text-transform: uppercase !important; letter-spacing: .6px !important; }
.pds__balance-value { font-size: 22px !important; font-weight: 800 !important; color: var(--pd-green, #0A5C36) !important; margin-bottom: 4px !important; }
.pds__balance-hint  { font-size: 10px !important; color: var(--pd-text-muted, #94A3B8) !important; display: block !important; }

/* MODAL HOVER EFFECTS */
.pds__kpi--clickable { cursor: pointer !important; transition: transform 0.15s, box-shadow 0.15s !important; }
.pds__kpi--clickable:hover { transform: translateY(-3px) !important; box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important; border-color: #CBD5E1 !important; }

/* DARK MODE */
#packaging-dashboard.pd--dark .pds__balance-card { background: #1E293B !important; border-color: #334155 !important; }
#packaging-dashboard.pd--dark .pds__balance-card-top { color: #94A3B8 !important; }
#packaging-dashboard.pd--dark .pds__balance-card-top i { color: #FBBF24 !important; }
#packaging-dashboard.pd--dark .pds__balance-value { color: #34D399 !important; }
#packaging-dashboard.pd--dark .pds__balance-hint { color: #64748B !important; }
</style>
